import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart as PieChartIcon, TrendingUp } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
const AREA_COLORS = {
  support_n1: '#3b82f6',
  quality: '#10b981',
  sdr: '#f59e0b',
  projects: '#8b5cf6'
};

export default function AnalyticsCharts({ overview, evolution }) {
  if (!overview) {
    return (
      <Card className="glass border-0 shadow-premium">
        <CardContent className="py-12 text-center">
          <PieChartIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Carregando análises...</p>
        </CardContent>
      </Card>
    );
  }

  // Prepare data for charts
  const pieData = Object.entries(overview.labels || {}).map(([name, value]) => ({
    name,
    value
  }));

  const barData = [
    { name: 'Suporte N1', value: overview.compatibility?.support_n1 || 0 },
    { name: 'Qualidade', value: overview.compatibility?.quality || 0 },
    { name: 'SDR', value: overview.compatibility?.sdr || 0 },
    { name: 'Projetos', value: overview.compatibility?.projects || 0 }
  ];

  return (
    <div className="space-y-8">
      <Card className="glass border-0 shadow-premium">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-indigo-600" />
            Análise Comportamental — Cíntia 👩‍💻
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            Visão estratégica dos perfis DISC identificados e compatibilidade com áreas
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pie Chart */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Distribuição de Perfis</h3>
              {pieData.length > 0 ? (
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${entry.value}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500 bg-gray-50 rounded-lg">
                  Nenhum candidato avaliado ainda
                </div>
              )}
            </div>

            {/* Bar Chart */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Média de Compatibilidade por Área</h3>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-15} textAnchor="end" height={80} />
                    <YAxis domain={[0, 100]} />
                    <RechartsTooltip />
                    <Bar dataKey="value" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Evolution Chart */}
      {evolution && evolution.length > 0 && (
        <Card className="glass border-0 shadow-premium">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Evolução de Compatibilidade (Últimos {evolution.length} Candidatos)
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Acompanhe a evolução das compatibilidades dos candidatos mais recentes
            </p>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 350 }}>
              <ResponsiveContainer>
                <LineChart data={evolution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="candidate" angle={-15} textAnchor="end" height={80} />
                  <YAxis domain={[0, 100]} />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="support_n1" stroke={AREA_COLORS.support_n1} name="Suporte N1" strokeWidth={2} />
                  <Line type="monotone" dataKey="quality" stroke={AREA_COLORS.quality} name="Qualidade" strokeWidth={2} />
                  <Line type="monotone" dataKey="sdr" stroke={AREA_COLORS.sdr} name="SDR" strokeWidth={2} />
                  <Line type="monotone" dataKey="projects" stroke={AREA_COLORS.projects} name="Projetos" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="glass border-0 shadow-hover">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Média Fator D</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-red-600">{overview.distribution?.D || 0}%</p>
            <p className="text-xs text-gray-500 mt-1">Dominância</p>
          </CardContent>
        </Card>

        <Card className="glass border-0 shadow-hover">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Média Fator I</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-yellow-600">{overview.distribution?.I || 0}%</p>
            <p className="text-xs text-gray-500 mt-1">Influência</p>
          </CardContent>
        </Card>

        <Card className="glass border-0 shadow-hover">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Média Fator S</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">{overview.distribution?.S || 0}%</p>
            <p className="text-xs text-gray-500 mt-1">Estabilidade</p>
          </CardContent>
        </Card>

        <Card className="glass border-0 shadow-hover">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Média Fator C</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-blue-600">{overview.distribution?.C || 0}%</p>
            <p className="text-xs text-gray-500 mt-1">Conformidade</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
